<template>
  <div style="min-height: 53px" class="mb-2">
    <div
      v-show="!loadingIntent"
      id="solid-payment-form-container"
      class="card-form-container"
    />
    <div
      v-if="loadingIntent || initializing"
      style="height: 53px"
      class="d-flex justify-center align-center mb-2"
    >
      <v-progress-circular indeterminate color="black" size="24" width="3" />
    </div>
  </div>
</template>

<script>
import { applePayAvailable } from '@/utils/paymentMethods';
import { resolveSurfaceTone } from '@/helpers/lightOrDark.js';

export default {
  name: 'SolidGateWallet',
  data: () => ({
    form: null,
    initializing: false,
  }),
  computed: {
    solidGatePaymentIntent() {
      return this.$store.getters.solidGatePaymentIntent;
    },
    loadingIntent() {
      return this.$store.getters.loadingSolidGatePaymentIntent;
    },
    signature() {
      return this.$store.getters.solidGatePaymentIntentSignature;
    },
    merchantData() {
      const { solidgate_data } = this.solidGatePaymentIntent;

      return {
        merchant: solidgate_data.merchant_id,
        signature: solidgate_data.signature,
        paymentIntent: solidgate_data.payment_intent,
      };
    },
    isAndroid() {
      const userAgent = navigator.userAgent.toLowerCase();
      return userAgent.includes('android');
    },
    isDev() {
      const env = process.env.NODE_ENV;

      return env === 'development';
    },
    showApplePay() {
      if (this.isDev) {
        return true;
      }

      return applePayAvailable();
    },
    showGooglePay() {
      if (this.isDev) {
        return true;
      }
      return !this.showApplePay;
    },
  },
  watch: {
    signature(newSignature, oldSignature) {
      // Update the wallet form if the signature changes
      if (newSignature !== oldSignature) {
        this.initWallet();
      }
    },
  },
  mounted() {
    // Initialize the wallet form last
    // this is a workaround for the issue with the iframe not loading properly
    // when the component is mounted
    // and the form is not ready yet.
    this.$nextTick(() => {
      this.initWallet();
    });
  },
  destroyed() {
    this.destroyWallet();
  },
  methods: {
    destroyWallet() {
      if (this.form) {
        this.form.unsubscribeAll();
        this.form = null;
      }
    },
    // Google branding requires the light button on dark backgrounds.
    resolveButtonColor() {
      return resolveSurfaceTone(this.$el) === 'dark' ? 'white' : 'black';
    },
    initWallet() {
      const merchantId = this.merchantData.merchant;

      this.initializing = true;

      if (!merchantId) {
        console.warn('Merchant ID is not available');
        this.initializing = false;
        return;
      }

      const buttonColor = this.resolveButtonColor();

      const walletData = {
        merchantData: this.merchantData,
        iframeParams: {
          width: '100%',
          // containerId: DO NOT USE this property causes weird issues
        },
        formParams: {
          enabled: false,
        },
        googlePayButtonParams: {
          enabled: this.showGooglePay,
          buttonType: 'pay',
          // containerId: DO NOT USE this property causes weird issues
          buttonColor,
          color: buttonColor,
          type: 'pay',
        },
        applePayButtonParams: {
          enabled: this.showApplePay,
          // containerId: DO NOT USE this property causes weird issues
          type: 'pay',
          buttonColor: 'black',
          color: 'black',
        },
      };
      // eslint-disable-next-line no-undef
      const walletForm = PaymentFormSdk.init(walletData);

      walletForm.on('success', data => {
        this.$emit('on-success', data);
        this.$store.dispatch('resetSolidGatePaymentIntent');

        this.destroyWallet();
      });
      walletForm.on('fail', error => {
        this.$emit('on-error', error.data);
      });
      walletForm.on('error', error => {
        this.$emit('on-error', error.data);
      });
      walletForm.on('submit', data => {
        this.$emit('on-submit', data);
      });
      walletForm.on('mounted', () => {
        this.initializing = false;
      });
      walletForm.mount('error', error => {
        console.error('Error mounting wallet form:', error);
        this.initializing = false;
      });

      this.form = walletForm;
    },
  },
};
</script>

<style scoped lang="scss">
.card-form-container {
  margin-bottom: 8px;
  iframe {
    border: none !important;
  }
}
</style>
